/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionClosePopover';

const ActionClosePopoverManager: ActionManagerFn<'ActionClosePopover'> = (
  platform: Platform,
): ActionManager<'ActionClosePopover'> => {
  return {
    ...baseActionManager(platform, 'ActionClosePopover'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Popover Item ID',
        type: 'ItemID',
        initialFilter: 'Popover',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Popover') {
            return `Item id '${value}' doesn't belong to a Popover`;
          }
          return '';
        },
      },
    },
    replaceableWith: ['ActionOpenPopover'],
    name: 'Close Popover',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'times' },
    description: `**Overview**<br/>
The Close Popover action is a UI management component designed to programmatically dismiss a floating "Popover" element. It targets a specific popover instance within the interface and forces it to close, ensuring temporary contextual overlays can be removed as part of user logic or interaction flows.<br/><br/>
This component incorporates strict validation mechanisms to ensure it only interacts with valid Popover components. It is explicitly linked to the ActionOpenPopover component in the registry, allowing for easy replacement or toggling between open and close actions during development.

**Key Features**<br/>
* **Targeted Instance Management:** Operates on a specific component identifier (targetItemId), allowing the application to manage multiple distinct popovers simultaneously.
* **Type Safety Validation:** Uses validateItemId to verify that the provided ID corresponds to a component of type 'Popover', preventing incorrect targeting.
* **Component Interchangeability:** Defined in the replaceableWith array, enabling quick swapping with ActionOpenPopover within the editor.
* **Registry Filtering:** Uses initialFilter: 'Popover' to restrict selection tooling to relevant popover components.

**Properties**<br/>
* **Popover Item ID (targetItemId):** An ItemID property specifying the popover to close.
  * *Validation:* Confirms the item exists in the registry and item.type === 'Popover'.
  * *Error Message:* Returns "Item id '[value]' doesn't belong to a Popover" if validation fails.

**Use Cases**<br/>
* **Selection Confirmation:** Closing a selection popover (e.g., date picker, color chooser) immediately after the user makes a valid choice.
* **Dismissal Controls:** Assign to a "Cancel" or "Close" button inside the popover content to allow manual dismissal.`,
  };
};

export default ActionClosePopoverManager;
